import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import {
  siteInfo,
  siteStats,
  principles,
  audiences,
  testimonials,
  updateProcess,
} from "@/mocks/site";

const SEO_TITLE = "关于我们 - 网报吃瓜的定位、内容原则与更新机制";
const SEO_DESC =
  "了解网报吃瓜是什么：一个只搬运官方通报与辟谣原文、把碎片信息还原成时间线的信息索引站。包含网站定位、内容原则、核实流程与联系方式。";
const SEO_KEYWORDS = "网报吃瓜官方,网报吃瓜是什么,官方通报索引,辟谣时间线,信息核实";

export default function About() {
  useSeo({
    title: SEO_TITLE,
    description: SEO_DESC,
    keywords: SEO_KEYWORDS,
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: SEO_TITLE,
          description: SEO_DESC,
          url: "https://sxdayun.com.cn/about",
        },
        {
          "@type": "Organization",
          name: "网报吃瓜",
          url: "https://sxdayun.com.cn/",
          email: "contact@wangbao-chigua.site",
          description: "收录官方通报、声明、回应与辟谣原文，整理成可复盘时间线的中文信息索引站。",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://sxdayun.com.cn/" },
            { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sxdayun.com.cn/about" },
          ],
        },
      ],
    },
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-14">
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  关于网报吃瓜
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {siteInfo.intro}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  我们把自己定位成一份「公开信息的索引」，而不是观点输出者。官方的通报、声明、回应与辟谣只要公开可查，就按时间摆进时间线；没有官方发声的部分，会明确标注「暂无官方回应」，不替任何人下结论。
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/events"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-list-check-3"></i>
                    </span>
                    浏览事件追踪
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-mail-line"></i>
                    </span>
                    联系我们
                  </Link>
                </div>
              </div>
              <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20stack%20of%20printed%20documents%20and%20desk%20lamp%20on%20wooden%20table%2C%20soft%20morning%20light%2C%20muted%20amber%20and%20cream%20tones%2C%20calm%20minimal%20photography%20style&width=1000&height=800&seq=chigua-about-01&orientation=landscape"
                  alt="网报吃瓜内容整理工作台，桌面上的官方文件与台灯"
                  title="网报吃瓜 · 内容整理与核对"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-2 gap-6 border-y border-background-200 py-8 lg:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label}>
                  <div className="font-heading text-3xl font-bold text-foreground-950">
                    {stat.value}
                    <span className="ml-1 text-sm font-normal text-foreground-500">{stat.unit}</span>
                  </div>
                  <div className="mt-1 text-xs text-foreground-600 md:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
              <div>
                <span className="text-xs font-semibold tracking-[0.2em] text-accent-600">
                  POSITION
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                  网站定位
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  面向想快速弄清「官方到底怎么说」的普通网友、需要核实信息再转发的内容创作者，以及希望给出可信依据的社群管理者。
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {audiences.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-100/60 p-5"
                  >
                    <h3 className="font-heading text-sm font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14">
              <span className="text-xs font-semibold tracking-[0.2em] text-accent-600">
                PRINCIPLES
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                内容原则
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {principles.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
              <div>
                <span className="text-xs font-semibold tracking-[0.2em] text-accent-600">
                  WORKFLOW
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                  核实与更新流程
                </h2>
                <ol className="mt-6 flex flex-col gap-5">
                  {updateProcess.map((step) => (
                    <li key={step.step} className="flex gap-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 font-heading text-sm font-bold text-secondary-700">
                        {step.step}
                      </span>
                      <div>
                        <h3 className="font-heading text-sm font-bold text-foreground-950">
                          {step.title}
                        </h3>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                          {step.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <span className="text-xs font-semibold tracking-[0.2em] text-accent-600">
                  VOICES
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                  用户口碑
                </h2>
                <div className="mt-6 flex flex-col gap-4">
                  {testimonials.map((item) => (
                    <figure
                      key={item.name}
                      className="rounded-lg border border-background-200 bg-background-100/60 p-5"
                    >
                      <blockquote className="text-sm leading-relaxed text-foreground-700">
                        {item.text}
                      </blockquote>
                      <figcaption className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-heading font-bold text-primary-700">
                          {item.name.slice(0, 1)}
                        </span>
                        <span className="font-medium text-foreground-800">{item.name}</span>
                        <span>· {item.role}</span>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-14 flex flex-col items-start justify-between gap-5 rounded-lg border border-background-200 bg-secondary-50 p-6 md:flex-row md:items-center md:p-8">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  线索提交、内容纠错与转载合作，都可以通过邮件或联系页与我们沟通。{siteInfo.period}。
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${siteInfo.email}`}
                  rel="nofollow"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-mail-line"></i>
                  </span>
                  {siteInfo.email}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-edit-line"></i>
                  </span>
                  前往联系页
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}